import {Observable, Subscription, toArray} from 'rxjs';
import { tap } from "rxjs/operators";
import { CustomIconsService } from "./services/custom-icons.service";
import { NbIconLibraries } from "@nebular/theme";
import { CustomIconDataToRegister } from "./interfaces/custom-icon.interface";
import { customIconsMedicloud } from "src/assets/constants/icons";
import {
  AddressApiModel,
  AssessmentResult,
  AssessmentTypeEnum,
  EmployeeImportApiModel,
  OccupationCategorizationApiModel, OccupationImportApiModel, ReportType, PaymentType, TenantSettingApiModel, UserSettingApiModel, PaymentStatus
} from '../api-new';
import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";
import * as XLSX from "xlsx";
import * as moment from "moment";
import {UserRole} from './services/auth.service';

type Locale = 'sk' | 'en';

export interface Sort {
  prop: string;
  dir: string;
}

export interface EnrichedCapabilityResult {
  capabilityId: number;
  capabilityName: string;
  capabilityType: string;
  specification: string;
  code: string;
  validUntil: string | null;
  eligibleResult: 'ELIGIBLE' | 'LIMITED' | 'NONELIGIBLE';
  exclusive: boolean;
}

export interface EmployeeProfessionRow {
  employeeId: number;
  employeeTitleBeforeName?: string | null;
  employeeName: string;
  employeeSurname: string;
  employeeTitleAfterName?: string | null;
  occupationId: number;
  occupationName: string;
  capabilities: EnrichedCapabilityResult[];
}

export interface PersonNameApiModel {
  titleBeforeName?: string | null;
  name?: string | null;
  surname?: string | null;
  titleAfterName?: string | null;
  employeeTitleBeforeName?: string | null;
  employeeName?: string | null;
  employeeSurname?: string | null;
  employeeTitleAfterName?: string | null;
}

export function shiftDate(date: Date, delta: number): Date {
  return new Date(new Date(date).setDate(new Date(date).getDate() + delta));
}

export function formatPersonName(person?: PersonNameApiModel | null): string {
  if (!person) {
    return '';
  }

  const before = (person.titleBeforeName ?? person.employeeTitleBeforeName)?.trim();
  const firstName = (person.name ?? person.employeeName)?.trim();
  const lastName = (person.surname ?? person.employeeSurname)?.trim();
  const after = (person.titleAfterName ?? person.employeeTitleAfterName)?.trim();

  const mainName = [before, firstName, lastName].filter(Boolean).join(' ').trim();

  if (mainName && after) {
    return `${mainName}, ${after}`;
  }

  return mainName || after || '';
}

export function controlInputPrice(price: string) {
  let priceArray = price.replace(/[^0-9, ^,]/g, '').split(',');
  switch(priceArray.length) {
    case 1: {
      (priceArray[0].length === 2 && priceArray[0].charAt(0) === '0')
      ? price = priceArray[0].substring(0, 1)
      : price = priceArray[0]
      break;
    }
    case 2: {
      (priceArray[0].length === 0)
      ? price = priceArray[1]
      : (priceArray[1].length > 2)
        ? price = priceArray[0] + ',' + priceArray[1].substring(0, 2)
        : price = priceArray[0] + ',' + priceArray[1]
      break;
    }
    case 3: {
      price = priceArray[0] + ',' + priceArray[1];
      break;
    }
    default: break;
  }
  return price;
}

export function isDateValid(date: any) {
  return (Object.prototype.toString.call(date) === "[object Date]") && (!isNaN(date.getTime())) ;
}

export function controlInputCompanyId(input: String) {
  let companyId = input.replace(/[^0-9, ^,]/g, '').split(',')[0].replace(' ', '');
  if(companyId.length >= 8) companyId = companyId.substr(0, 8);
  return companyId;
}

export class SubscriptionContainer {
  private subs: Subscription[] = [];

  set add(s: Subscription) {
      this.subs.push(s);
  }

  dispose() {
      this.subs.forEach(s => s.unsubscribe());
  }
}

export function createAlias(companyName: string) {

    return companyName.trim().split(' ')[0].toLowerCase().substring(0, 12).trim();

}

export function customIconsInitializeFactory(
  iconLibraries: NbIconLibraries,
  customIconsService: CustomIconsService,
): () => Observable<CustomIconDataToRegister> {
  return () => customIconsService.iconDataToRegister$(customIconsMedicloud.iconList).pipe(
    tap((iconsDataToRegister) => iconLibraries.registerSvgPack(
      customIconsMedicloud.packName,
      iconsDataToRegister
    )),
  );
}

export function timeStringToMinutes(
  timeString: string // hh:mm
): number {
  const array = timeString.split(":");
  return (parseInt(array[0], 10) * 60) + (parseInt(array[1], 10));
}

export function dateToTimeString(
  date: Date
): string {
  let minutes = date.getMinutes().toString();
  minutes = minutes.length > 1 ? minutes : `0${minutes}`;
  return `${date.getHours()}:${minutes}`;
}

export function getMinutesFromDate(
  date: Date
): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function getHoursFromTimeString(
  timeString: string // hh:mm
): number {
  return parseInt(timeString.split(":")[0]);
}

export function resetTimeInDate(
  date: Date
): Date {
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
}

export function formatDate(data:string): string {
  if (data == '' || data == null) {
    return '';
  }
  const date = new Date(data);
  let day = date.getDate().toString();
  let month = (date.getMonth() + 1).toString();
  let year = date.getFullYear().toString();
  day = day.padStart(2, '0');
  month = month.padStart(2, '0');
  return `${day}.${month}.${year}`;
}

export enum typeOfItem {
  RISK_FACTOR = "riskFactor",
  WORK_ACTIVITY = "workActivity"
}

export function buildAddress(addressApiModel: AddressApiModel): string {
  if(addressApiModel == null) {
    return '';
  }
  const { streetName, cityName, zipValue, countryName, registerNumber, descriptiveNumber } = addressApiModel;
  const regAndDescNumbers = () => `${descriptiveNumber && registerNumber ? registerNumber + "/" + descriptiveNumber : (registerNumber ? "" + registerNumber : descriptiveNumber)}`;
  const fullStreet = () => `${streetName??''}${regAndDescNumbers() ? " " + regAndDescNumbers() : ""}`;
  const cityFirst = () => `${cityName??''}${regAndDescNumbers() ? " " + regAndDescNumbers() : ""}`;
  const fillZip = () => ("" + zipValue).replace(" ", "") + "     ";
  const formattedZip = () => (fillZip().substring(0, 3) + " " + fillZip().substring(3, 5)).trim();
  let addressFields: string[] = [];

  if (!streetName && !cityName && !zipValue && !countryName && !registerNumber && !descriptiveNumber) return "";

  if (streetName) {
    addressFields.push(fullStreet());
  } else {
    addressFields.push(cityFirst());
  }
  addressFields.push((formattedZip() + " " + cityName).trim());
  addressFields.push(`${countryName || "Slovensko"}`);
  return addressFields.filter(fld => fld).join(", ").trim();
}

export function buildAddressParts(addressApiModel: AddressApiModel): string[] {
  if (addressApiModel == null) {
    return [];
  }

  const { streetName, cityName, zipValue, countryName, registerNumber, descriptiveNumber } = addressApiModel;
  const regAndDescNumbers = () => `${descriptiveNumber && registerNumber ? registerNumber + "/" + descriptiveNumber : (registerNumber ? "" + registerNumber : descriptiveNumber)}`;
  const fullStreet = () => `${streetName ?? ''}${regAndDescNumbers() ? " " + regAndDescNumbers() : ""}`;
  const cityFirst = () => `${cityName ?? ''}${regAndDescNumbers() ? " " + regAndDescNumbers() : ""}`;
  const fillZip = () => ("" + zipValue).replace(" ", "") + "     ";
  const formattedZip = () => (fillZip().substring(0, 3) + " " + fillZip().substring(3, 5)).trim();

  if (!streetName && !cityName && !zipValue && !countryName && !registerNumber && !descriptiveNumber) {
    return [];
  }

  let addressFields: string[] = [];

  if (streetName) {
    addressFields.push(fullStreet());
  } else {
    addressFields.push(cityFirst());
  }
  addressFields.push((formattedZip() + " " + cityName).trim());
  addressFields.push(`${countryName || "Slovensko"}`);

  return addressFields.filter(fld => !!fld);
}


export function isDateInDateInterval(
  date: Date | string,
  start: Date | string,
  end: Date | string
): boolean {
  start = new Date((new Date(start)).toDateString());
  end = new Date((new Date(end)).toDateString());
  date = new Date(new Date(date).toDateString());
  return start <= date && date <= end;
}

export function getExaminationTypeName(type: AssessmentTypeEnum): string {
  return type === AssessmentTypeEnum.IMPORT ? 'Importovaná' :
    type === AssessmentTypeEnum.OUTPUT ? 'Výstupná' :
    type === AssessmentTypeEnum.INPUT ? 'Vstupná' :
    type === AssessmentTypeEnum.PERIOD ? 'Periodická' :
    type === AssessmentTypeEnum.REGULAR ? 'Jednorázová' : "NEZNAMY TYP PREHLIADKY";
}

export enum settingTypeOfCategory {
  COMPANY_SETTING = "companySetting",
  ACCOUNT_SETTING = "accountSetting",
  PAYMENT_SETTING = "paymentSetting",
}

export function getAssessmentResultText(type: AssessmentResult): string {
  switch (type) {
    case AssessmentResult.NONELIGIBLE:
      return "Nespôsobilý";
    case AssessmentResult.ELIGIBLE:
      return "Spôsobilý";
    case AssessmentResult.LIMITED:
      return "Spôsobilý s dočasným obmedzením";
    case AssessmentResult.NONE:
      return 'Neurčené';
    default:
      return type;
  }
}

export function calculateNextFreeDate(
  currentEndDate: Date,
  sortedCategorizations: OccupationCategorizationApiModel[],
  currentIndex: number
): Date | null {
  let nextFreeStartDate = new Date(currentEndDate.getTime() + 86400000);

  for (let i = currentIndex + 1; i < sortedCategorizations.length; i++) {
    const nextCatStart = new Date(sortedCategorizations[i].validSince);

    if (nextFreeStartDate < nextCatStart) {
      break;
    }

    nextFreeStartDate = new Date(
      sortedCategorizations[i].validUntil
        ? new Date(sortedCategorizations[i].validUntil!).getTime() + 86400000
        : nextCatStart.getTime() + 86400000
    );
  }

  return nextFreeStartDate;
}

export function calculateNextFreeDateWithoutEndDate(
  startDate: Date,
  sortedCategorizations: OccupationCategorizationApiModel[]
): Date | null {
  let nextFreeStartDate = new Date(startDate);

  for (let i = 0; i < sortedCategorizations.length; i++) {
    const catStart = new Date(sortedCategorizations[i].validSince);
    const catEnd = sortedCategorizations[i].validUntil
      ? new Date(sortedCategorizations[i].validUntil!)
      : null;

    if (nextFreeStartDate < catStart) {
      return nextFreeStartDate;
    }

    if (catEnd && nextFreeStartDate >= catStart) {
      nextFreeStartDate = new Date(catEnd.getTime() + 86400000);
    }
  }

  return nextFreeStartDate;
}

export function validateAutocomplete(options: any[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return {required: true};
    }
    const isValid = options.includes(control.value);
    return isValid ? null : {invalidSelection: true};
  };
}

export function setMidday(date: Date | string): Date {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes() - d.getTimezoneOffset());
}


export function dateValidator(control: AbstractControl): { [key: string]: any } | null {
  if (!control.value) {
    return { required: true };
  }



  const date = moment(control.value, 'DD.MM.YYYY', true);

  if (!date.isValid()) {
    return { invalidDate: true };
  }

  return null;
}

export const normalizeToUTC= (date: any): string | null => {
  if (!date) return null;
  if (date?._isAMomentObject) {
    date = date.toDate();
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day, 12, 0, 0)).toISOString();
};

export const mainPage = () => {
  window.open('https://www.medicloud.digital', 'noopener,noreferrer');
};

export const gdpr = () => {
  window.open('https://www.medicloud.digital/sk/gdpr', 'noopener,noreferrer');
};

export const vop = () => {
  window.open('https://www.medicloud.digital/sk/rule', 'noopener,noreferrer');
};


export function getFormatedDateOrTime(datetime: string): { date: string, time: string } {
  const dateObj = new Date(datetime);

  const pad = (n: number) => n.toString().padStart(2, '0');

  const date = `${pad(dateObj.getDate())}.${pad(dateObj.getMonth() + 1)}.${dateObj.getFullYear()}`;
  const time = `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;

  return { date, time };
}

export function decodeJwt(token: string): any {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch (e) {
    return null;
  }
}

export function maxDateValidator(maxDate: Date): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    return new Date(control.value) > maxDate
      ? { maxDate: true }
      : null;
  };
}

export function translateReportType(type: ReportType): string {
  switch (type) {
    case ReportType.EMPLOYEES_ELIGIBLE_REPORT:
      return 'Prehľad spôsobilosti zamestnancov';
    default:
      return type;
  }
}

export type SettingsBundle = {
  role: UserRole;
  userSettings: UserSettingApiModel[];
  tenantSettings: TenantSettingApiModel[];
};

export function translatePaymentStatus(
  status: PaymentStatus,
  locale: Locale = 'sk'
): string {
  const map: Record<Locale, Record<PaymentStatus, string>> = {
    sk: {
      WAITING: 'Čaká na platbu',
      PAID: 'Zaplatené',
      FAILED: 'Zlyhané',
      CANCELLED: 'Zrušené',
    },
    en: {
      WAITING: 'Waiting',
      PAID: 'Paid',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    },
  };
  return map[locale][status] ?? status;
}

export function translatePaymentType(
  type: PaymentType,
  locale: Locale = 'sk'
): string {
  const map: Record<Locale, Record<PaymentType, string>> = {
    sk: {
      AccountPlan: 'Plán účtu',
    },
    en: {
      AccountPlan: 'Account plan',
    },
  };

  return map[locale][type] ?? type;
}

export function buildEligibilityCsv(
  rows: EmployeeProfessionRow[],
  dataset: { employees: any[]; capabilities: any[] }
): string {
  const capsById = new Map<number, any>((dataset.capabilities || []).map((c: any) => [c.id, c]));
  const employeeIndex = new Map<number, any>((dataset.employees || []).map((e: any) => [e.employeeId, e]));

  const mapStatus = (res?: 'ELIGIBLE' | 'LIMITED' | 'NONELIGIBLE' | string) => {
    if (res === 'ELIGIBLE') return 'Sposobilý';
    if (res === 'LIMITED') return 'Dočasné obmedzenie';
    if (res === 'NONELIGIBLE') return 'Nesposobilý';
    return 'Neposúdené';
  };

  const fmtDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const csvSafe = (v: any) =>
    `"${(v ?? '').toString().replace(/\r?\n|\r/g, ' ').replace(/"/g, '""')}"`;

  const header = ['Meno', 'Profesia', 'Spôsobilosť', 'Stav spôsobilosti', 'Platnosť do'];
  const lines: string[] = [header.join(';')];

  for (const r of rows) {
    const fullName = formatPersonName(r);
    const profession = r.occupationName ?? '';

    const combinedCaps: EnrichedCapabilityResult[] = [...(r.capabilities || [])];
    const existingIds = new Set<number>(combinedCaps.map(c => c.capabilityId));

    const srcEmp = employeeIndex.get(r.employeeId);
    const exclusiveIds: number[] = srcEmp?.exclusiveCapabilities || [];
    const eligibleResults: any[] = srcEmp?.capabilityEligibleResults || [];

    for (const exId of exclusiveIds) {
      if (existingIds.has(exId)) continue;

      const meta = capsById.get(exId);
      if (!meta) continue;

      const match = eligibleResults.find(er => er.capabilityId === exId);
      combinedCaps.push({
        capabilityId: exId,
        capabilityName: meta?.name ?? 'Unknown',
        capabilityType: meta?.type ?? 'Unknown',
        specification: meta?.specification ?? '',
        code: meta?.code ?? '',
        validUntil: match?.validUntil ?? null,
        eligibleResult: (match?.eligibleResult as any) ?? 'NONELIGIBLE',
        exclusive: true
      });
      existingIds.add(exId);
    }

    for (const cap of combinedCaps) {
      const meta = capsById.get(cap.capabilityId);
      const label =
        `${cap.capabilityName ?? meta?.name ?? ''}` +
        ((meta?.category != null) ? ` - ${meta.category}` : '');

      const status = mapStatus(cap.eligibleResult);
      const validUntil = fmtDate(cap.validUntil || null);

      lines.push([
        csvSafe(fullName),
        csvSafe(profession),
        csvSafe(label),
        csvSafe(status),
        csvSafe(validUntil),
      ].join(';'));
    }
  }

  return '\uFEFF' + lines.join('\r\n');
}

export function getFilenameFromContentDisposition(contentDisposition: string): string {
  let filename = 'default-filename';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

  // Parse filename*= if present (prioritized for UTF-8)
  const utf8FilenameRegex = /filename\*=[^']*''([^;]*)/;
  const utf8FilenameMatch = contentDisposition.match(utf8FilenameRegex);
  if (utf8FilenameMatch && utf8FilenameMatch[1]) {
    return decodeURIComponent(utf8FilenameMatch[1]);
  }

  // Parse filename= as a fallback
  const matches = contentDisposition.match(filenameRegex);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '');
  }
  return filename;
}

export function buildRowsForCsv(report: any): EmployeeProfessionRow[] {
  const employees: any[] = (report as any).employees || [];
  const capabilities: any[] = (report as any).capabilities || [];
  const capabilitiesMap = new Map<number, any>(
    capabilities.map((c: any) => [c.id, c])
  );

  const rows: EmployeeProfessionRow[] = [];

  for (const emp of employees) {
    const employeeTitleBeforeName = emp.employeeTitleBeforeName ?? '';
    const employeeName = emp.employeeName ?? '';
    const employeeSurname = emp.employeeSurname ?? '';
    const employeeTitleAfterName = emp.employeeTitleAfterName ?? '';
    const occupations = emp.occupations || [];
    const eligibleResults: any[] = emp.capabilityEligibleResults || [];
    const exclusiveCapabilities: number[] = emp.exclusiveCapabilities || [];

    for (const occ of occupations) {
      const occupationId = occ.occupationId;
      const occupationName = occ.occupationName ?? '';
      const capIds: number[] = occ.capabilityIds || [];

      const includedCapIds = new Set<number>();

      const mainCapabilities: EnrichedCapabilityResult[] = capIds.map((capId: number) => {
        includedCapIds.add(capId);
        const capability = capabilitiesMap.get(capId);
        const match = eligibleResults.find((res: any) => res.capabilityId === capId);

        return {
          capabilityId: capId,
          capabilityName: capability?.name ?? 'Unknown',
          capabilityType: capability?.type ?? 'Unknown',
          specification: capability?.specification ?? '',
          code: capability?.code ?? '',
          validUntil: match?.validUntil ?? null,
          eligibleResult: (match?.eligibleResult as any) ?? 'NONELIGIBLE',
          exclusive: false,
        };
      });

      const exclusiveExtra: EnrichedCapabilityResult[] = exclusiveCapabilities
        .filter((capId: number) => !includedCapIds.has(capId))
        .map((capId: number) => {
          includedCapIds.add(capId);
          const capability = capabilitiesMap.get(capId);
          const match = eligibleResults.find((res: any) => res.capabilityId === capId);

          return {
            capabilityId: capId,
            capabilityName: capability?.name ?? 'Unknown',
            capabilityType: capability?.type ?? 'Unknown',
            specification: capability?.specification ?? '',
            code: capability?.code ?? '',
            validUntil: match?.validUntil ?? null,
            eligibleResult: (match?.eligibleResult as any) ?? 'NONELIGIBLE',
            exclusive: true,
          };
        });

      const fullRow: EmployeeProfessionRow = {
        employeeId: emp.employeeId,
        employeeTitleBeforeName,
        employeeName,
        employeeSurname,
        employeeTitleAfterName,
        occupationId,
        occupationName,
        capabilities: [...mainCapabilities, ...exclusiveExtra],
      };

      rows.push(fullRow);
    }
  }

  return rows;
}

export function downloadCsv(csvContent: string, requestDate: moment.Moment): void {
  const fileName =
    `${requestDate.format('YYYYMMDD')}_export spôsobilostí.csv`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const  FilterExpired = "filterExpired";
export const ShowOnlyExpired = "showOnlyExpired";
export const ShowOnlyEligible = "showOnlyEligible";
export const currentTabIndex = 'currentTabIndex';

export const supportEmail = "support@medicloud.digital";
